import { FC, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/helper/api";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import GenericNotification from "@/components/notification/genericNotification";
import { useTranslation } from "react-i18next";
import { Chip, FormControl, Paper, Tooltip, Typography } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import TinyMceEditor, {
  type TinyMceEditorHandle,
} from "@/components/tinymce/tinymce";
import { InputWithRef } from "@/components/forms/inputsWithRef/inputWithRef";
import styles from "@/components/mailTemplate/mailTemplate.module.css";
import { TextArea } from "@/components/forms/inputs/textArea";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { useConfirmationModal } from "@/context/confirmationModalContext";
import GenericControllerWithInput from "@/components/forms/inputsWithController/GenericController";
import DropDown from "@/components/forms/inputs/dropDown";
import useFocusFirstInvalidField from "@/helper/customHooks/useFocusFirstInvalidField";
import TemplateMailPreferences from "@/components/mailTemplate/templateMailPreferences";
import Restricted from "@/consumer/restricted";

type EditorType = "editor" | "textarea";

const MailTemplateEditor: FC = () => {
  const { handleError, errorModal } = useErrorModal();
  const [showNotification, setShowNotification] = useState<boolean>(false);
  const [notificationMessage, setNotificationMessage] = useState<any>("");
  const [notificationVariant, setNotificationVariant] = useState<
    "success" | "error" | "warning" | "info" | undefined
  >();
  const [mailTemplateName, setMailTemplateName] = useState<any>("");
  const [mailTemplateType, setMailTemplateType] = useState<any>("");
  const [templateTraceId, setTemplateTraceId] = useState<string>("");

  const [mailTemplateTags, setMailTemplateTags] = useState<any>([]);
  const [mailTemplateAvailableTypes, setMailTemplateAvailableTypes] =
    useState<any>([]);

  const [previewContent, setPreviewContent] = useState("");
  const [wrapperTemplateForPreview, setWrapperTemplateFotPreview] =
    useState<any>(null);
  const [editortype, setEditorType] = useState<EditorType>("editor");
  const editorRef = useRef<TinyMceEditorHandle | null>(null);

  const { register, handleSubmit, control, watch, formState, reset } =
    useForm<any>({ shouldFocusError: false });
  const { registerValidationTarget, focusFirstInvalidField } =
    useFocusFirstInvalidField({ fieldOrder: ["name"] });

  const { mailTemplateId } = useParams() as { mailTemplateId: string };
  const navigate = useNavigate();
  const { isDirty } = formState;
  const { showConfirmationModalAsync } = useConfirmationModal();
  const { t } = useTranslation();

  const handleNavigate = async () => {
    if (isDirty) {
      const isConfirmed = await showConfirmationModalAsync(
        t("confirm_go_back")
      );
      if (!isConfirmed) {
        return;
      }
    }
    navigate("/settings/mailTemplateList");
  };
  useEffect(() => {
    getAvailableMailTemplateTags();
  }, [mailTemplateType]);
  useEffect(() => {
    getMailTemplateTypes();
  }, []);

  useEffect(() => {
    if (mailTemplateId !== undefined) {
      getMailTemplate();
    }
  }, [mailTemplateId]);

  useEffect(() => {
    if (mailTemplateName !== "") {
      getMailPreviewWrapperTemplate();
    }
  }, [mailTemplateName]);

  const resetStateOnCloseNotification = () => {
    setShowNotification(false);
  };
  const getMailPreviewWrapperTemplate = () => {
    const additionalUrlParameter: any = {};
    additionalUrlParameter["name__in"] = "wrapper_template";
    api.genericApiRequest({
      method: "get",
      entity: "mailTemplate",
      parametersToRender: { additionalUrlParameters: additionalUrlParameter },
      successHandler: (res: any) => {
        if (res.data.results.length !== 0) {
          const wrapperForPreview = res.data.results[0].mail_body;
          setWrapperTemplateFotPreview(wrapperForPreview);
        }
      },

      failHandler: (error: any) => {
        handleError(error);
      },
    });
  };

  const getMailTemplate = () => {
    api.genericApiRequest({
      method: "get",
      entity: "mailTemplate",
      entityId: mailTemplateId,
      successHandler: (res: any) => {
        const data = res.data;
        setTemplateTraceId(data.trace_id);
        setMailTemplateType(data.type);
        setMailTemplateName(data.name);
        reset({
          name: data.name || "",
          mail_subject: data.mail_subject || "",
          mail_body: data.mail_body || "",
          type: data.type || "",
        });

        if (data.name === "wrapper_template") {
          setEditorType("textarea");
        }
      },
      failHandler: (error: any) => {
        handleError(error);
      },
    });
  };
  const getMailTemplateTypes = () => {
    const additionalRouteParts: any = {};
    additionalRouteParts["available_types"] = "/";
    api.genericApiRequest({
      method: "get",
      entity: "mailTemplate",
      additionalRouteParts: additionalRouteParts,
      successHandler: (res: any) => {
        const data = res.data.results;
        const tempTypes = data.map((type: any) => {
          return { value: type, label: type };
        });
        setMailTemplateAvailableTypes(tempTypes);
      },
      failHandler: (error: any) => {
        handleError(error);
      },
    });
  };

  const getAvailableMailTemplateTags = () => {
    const additionalUrlParameter: any = {};
    additionalUrlParameter["type"] = mailTemplateType;
    const additionalRouteParts: any = {};
    additionalRouteParts["available_tags"] = "/";

    if (mailTemplateName !== "") {
      api.genericApiRequest({
        entity: "mailTemplate",
        method: "get",
        parametersToRender: {
          depth: "0",
          additionalUrlParameters: additionalUrlParameter,
        },
        additionalRouteParts: additionalRouteParts,
        successHandler: (res: any) => {
          setMailTemplateTags(res.data.results);
        },
        failHandler: (error: any) => {
          handleError(error);
        },
      });
    }
  };

  const submitMailTemplate = (data: any) => {
    const result = { ...data, type: data.type?.value };

    const config = {
      headers: {
        "Content-Type": "application/json",
      },
    };
    api.genericApiRequest({
      method: "put",
      entity: "mailTemplate",
      entityId: mailTemplateId,
      config: config,
      submitData: result,
      successHandler: (res: any) => {
        setShowNotification(true);
        setNotificationMessage(t("save_successful"));
        setNotificationVariant("success");
        navigate(`/settings/mailTemplateEditor/${res.data.id}`);
      },
      failHandler: (error: any) => {
        handleError(error);
      },
    });
  };

  const insertTag = (tag: string) => {
    editorRef.current?.insertContent(`{{ ${tag} }}`);
  };

  const tagList = mailTemplateTags.map((name: any, index: any) => {
    const isTinyMceEditor = editortype === "editor";

    return (
      <Chip
        key={name + index}
        label={name}
        className={styles.taglist_chipItem}
        onMouseDown={
          isTinyMceEditor ? (event) => event.preventDefault() : undefined
        }
        onClick={isTinyMceEditor ? () => insertTag(name) : undefined}
      />
    );
  });

  const handleContentChange = (inputValue: any) => {
    if (wrapperTemplateForPreview !== null) {
      const content =
        editortype === "editor" ? inputValue : inputValue.target.value;
      const updatedTemplate = wrapperTemplateForPreview.replace(
        "{{ mail_content }}",
        content
      );
      setPreviewContent(updatedTemplate);
    }
  };
  if (!wrapperTemplateForPreview) {
    return null;
  }

  return (
    <div
      style={{
        width: "100%",
      }}
    >
      <>
        <Tooltip
          placement="right"
          title={t("cancel") + " / " + t("back")}
          sx={{ position: "sticky", top: "0" }}
        >
          <GenericButton
            variant="icon"
            color="iconBase"
            onClick={handleNavigate}
          >
            <ArrowBackIcon />
          </GenericButton>
        </Tooltip>
        <Paper
          sx={{
            padding: "0.75rem",
            borderRadius: "0.75rem",
            maxWidth: "65%",
            margin: "0 auto",
            overflow: "scroll",
          }}
        >
          <form
            onSubmit={handleSubmit(submitMailTemplate, (errors) => {
              focusFirstInvalidField(errors);
            })}
            noValidate
          >
            <div ref={registerValidationTarget("name")}>
              <InputWithRef
                label={t("name")}
                {...register("name", {
                  required: t("field_required"),
                })}
              />
              {formState.errors.name && (
                <Typography color="error" variant="caption">
                  {String(formState.errors.name.message)}
                </Typography>
              )}
            </div>
            <InputWithRef
              label={t("mail_subject")}
              {...register("mail_subject")}
            />

            <GenericControllerWithInput
              control={control}
              name="type"
              renderInput={({ onChange, value }) => (
                <DropDown
                  options={mailTemplateAvailableTypes}
                  onChange={onChange}
                  name={t("type")}
                  label={t("type")}
                  defaultValue={value}
                />
              )}
            />

            {editortype === "editor" ? (
              <Controller
                name="mail_body"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <FormControl
                    fullWidth
                    className={styles.tinyMceEditorWrapper}
                  >
                    <TinyMceEditor
                      ref={editorRef}
                      showFooter={false}
                      value={value}
                      onChange={(content: any) => {
                        onChange(content);
                        handleContentChange(content);
                      }}
                    />
                  </FormControl>
                )}
              />
            ) : (
              <Controller
                name="mail_body"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <FormControl fullWidth>
                    <TextArea
                      rows={15}
                      fieldClassName={styles.textArea}
                      value={value}
                      onChange={(content: any) => {
                        onChange(content);
                        handleContentChange(content);
                      }}
                    />
                  </FormControl>
                )}
              />
            )}

            <section className={styles.taglist_container}>{tagList}</section>
            <section style={{ display: "flex", justifyContent: "flex-end" }}>
              <GenericButton type="submit">{t("save")}</GenericButton>
            </section>
          </form>
        </Paper>
        <Paper
          sx={{
            padding: "0.75rem",
            borderRadius: "0.75rem",
            maxWidth: "65%",
            margin: "2rem auto",
            overflow: "scroll",
            width: "100%",
          }}
        >
          <Typography variant="h5" color={"GrayText"}>
            {t("preview")}
          </Typography>
          <div
            dangerouslySetInnerHTML={{
              __html:
                mailTemplateName === "wrapper_template"
                  ? watch("mail_body")
                  : previewContent,
            }}
          />
        </Paper>
      </>
      {errorModal}

      {templateTraceId &&
        mailTemplateType !== "one_time" &&
        mailTemplateType !== "wrapper" && (
          <Restricted permissionRequired="change_mailtemplate">
            <TemplateMailPreferences traceId={templateTraceId} />
          </Restricted>
        )}
      <GenericNotification
        message={showNotification ? notificationMessage : null}
        variant={notificationVariant}
        handleCloseSnackbar={resetStateOnCloseNotification}
      />
    </div>
  );
};

export default MailTemplateEditor;
